import type { Category } from './category';
import type { MenuItem } from './menu-item';
import type { Translation } from './translations';
import type { Upgrade } from './upgrade';
import type { UpgradeItem } from './upgrade-item';

export const UPGRADE_TIER_STAGES = [1, 2, 3] as const;
export type UpgradeTierStage = (typeof UPGRADE_TIER_STAGES)[number];

const TIER_META_STRING_MAX_LENGTH = 200;

export interface UpgradeTierMeta {
  applyCategoryId?: string;
  tierNames: [string, string, string];
  tierImageUrls: [string, string, string];
}

function sanitizeTierMetaString(raw: unknown): string {
  if (typeof raw !== 'string') {
    return '';
  }
  return raw.trim().slice(0, TIER_META_STRING_MAX_LENGTH);
}

export function parseTierMeta(description?: string): UpgradeTierMeta {
  const empty: UpgradeTierMeta = {
    tierNames: ['', '', ''],
    tierImageUrls: ['', '', ''],
  };
  if (!description?.trim()) {
    return empty;
  }
  try {
    const parsedValue: unknown = JSON.parse(description);
    if (parsedValue === null || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return empty;
    }
    const parsedObject = parsedValue as Record<string, unknown>;
    const tierNamesRaw = parsedObject['tierNames'];
    if (tierNamesRaw !== undefined && !Array.isArray(tierNamesRaw)) {
      return empty;
    }
    if (Array.isArray(tierNamesRaw) && tierNamesRaw.length >= 3) {
      const tierNames: [string, string, string] = [
        sanitizeTierMetaString(tierNamesRaw[0]),
        sanitizeTierMetaString(tierNamesRaw[1]),
        sanitizeTierMetaString(tierNamesRaw[2]),
      ];
      const urlRowUnknown = parsedObject['tierImageUrls'];
      const tierImageUrls: [string, string, string] = [
        sanitizeTierMetaString(Array.isArray(urlRowUnknown) ? urlRowUnknown[0] : undefined),
        sanitizeTierMetaString(Array.isArray(urlRowUnknown) ? urlRowUnknown[1] : undefined),
        sanitizeTierMetaString(Array.isArray(urlRowUnknown) ? urlRowUnknown[2] : undefined),
      ];
      let applyCategoryId: string | undefined;
      const applyCategoryCandidate = parsedObject['applyCategoryId'];
      if (typeof applyCategoryCandidate === 'string' && applyCategoryCandidate.trim()) {
        applyCategoryId = sanitizeTierMetaString(applyCategoryCandidate) || undefined;
      }
      return { tierNames, tierImageUrls, applyCategoryId };
    }
    const applyOnly = parsedObject['applyCategoryId'];
    if (typeof applyOnly === 'string' && applyOnly.trim()) {
      return {
        ...empty,
        applyCategoryId: sanitizeTierMetaString(applyOnly) || undefined,
      };
    }
  } catch (error) {
    console.warn('[parseTierMeta] Failed to parse tier metadata:', error);
  }
  return empty;
}

export function categoryTranslationName(
  categories: Category[],
  categoryId: string,
  languageCode: string
): string {
  const category = categories.find((candidate) => candidate.id === categoryId);
  if (!category) {
    return '';
  }
  const translation =
    category.translations?.find((entry) => entry.languageCode === languageCode) ??
    category.translations?.[0];
  return translation?.name?.trim() ?? '';
}

export function menuItemForLanguage(
  allMenuItems: MenuItem[],
  itemId: string,
  languageCode: string
): MenuItem | undefined {
  const rowsForMenuItemId = allMenuItems.filter((menuItemRow) => menuItemRow.id === itemId);
  if (rowsForMenuItemId.length === 0) {
    return undefined;
  }
  return (
    rowsForMenuItemId.find((menuItemRow) => menuItemRow.languageCode === languageCode) ??
    rowsForMenuItemId[0]
  );
}

function isItemSellable(menuItem: MenuItem | undefined): boolean {
  if (!menuItem) {
    return false;
  }
  const menuItemCore = menuItem.menuItem;
  if (menuItemCore.available === false) {
    return false;
  }
  if (menuItemCore.stockEnabled === true && menuItemCore.stock === 0) {
    return false;
  }
  return true;
}

export function mapUpgradeItemsByStage(upgrade: Upgrade): Map<number, UpgradeItem> {
  return new Map(
    (upgrade.items ?? []).map(
      (upgradeItem: UpgradeItem) => [Number(upgradeItem.stage) || 0, upgradeItem] as const
    )
  );
}

export function tierSummaryNamesForStages(
  upgradeItemsByStage: Map<number, UpgradeItem>,
  allMenuItems: MenuItem[],
  languageCode: string
): string[] | null {
  const tierSummaryNames: string[] = [];
  for (const tierStage of UPGRADE_TIER_STAGES) {
    const upgradeItemRow = upgradeItemsByStage.get(tierStage);
    if (!upgradeItemRow) {
      return null;
    }
    if (
      !upgradeItemRow.itemId ||
      !Number.isFinite(upgradeItemRow.price) ||
      (upgradeItemRow.price ?? 0) <= 0
    ) {
      return null;
    }
    const localizedMenuItem = menuItemForLanguage(
      allMenuItems,
      upgradeItemRow.itemId,
      languageCode
    );
    if (!isItemSellable(localizedMenuItem) || !localizedMenuItem) {
      return null;
    }
    if (!localizedMenuItem.name?.trim()) {
      return null;
    }
    tierSummaryNames.push(localizedMenuItem.name.trim());
  }
  return tierSummaryNames.length === 3 ? tierSummaryNames : null;
}

export function tierCardDisplayTitleForStage(
  catalogUpgrade: Upgrade,
  tierStage: UpgradeTierStage,
  languageCode: string,
  allMenuItems: MenuItem[],
  categories: Category[]
): string {
  const translations = catalogUpgrade.translations ?? [];
  const translation =
    translations.find((entry: Translation) => entry.languageCode === languageCode) ??
    translations[0];
  const fallbackUpgradeName = translation?.name?.trim() ?? '';
  const meta = parseTierMeta(translation?.description);
  const upgradeItemsByStage = mapUpgradeItemsByStage(catalogUpgrade);
  const tierSummaryNamesResolved = tierSummaryNamesForStages(
    upgradeItemsByStage,
    allMenuItems,
    languageCode
  );
  if (!tierSummaryNamesResolved) {
    return fallbackUpgradeName;
  }
  const tierSummaryNames = tierSummaryNamesResolved as [string, string, string];
  const tierIndex = tierStage - 1;
  const tierName = meta.tierNames[tierIndex]?.trim();
  return tierName || tierSummaryNames[tierIndex] || fallbackUpgradeName;
}
